import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";
import { useNavigate } from "react-router-dom";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import { PageListContainer, PageCreateButton } from "../../../components/Page";
import DataTableWithPagination from "../../../components/DataTable";
import RightDrawer from "../../../components/RightDrawer";
import { DrawerForm, DrawerFormSection } from "../../../components/DrawerForm";
import { IconAutocomplete } from "../../../components/forms";
import {
  AssignSubscriptionRequest,
  DurationType,
  PeriodQuota,
  QuotaStatusResponse,
  SubscriptionPlan,
  UserSubscription,
} from "../../../types/subscription";
import { subscriptionPlanService } from "../../../services/subscriptionPlanService";
import { userSubscriptionService } from "../../../services/userSubscriptionService";
import { quotaService } from "../../../services/quotaService";
import {
  drawerButtonCancelProps,
  drawerButtonSaveProps,
  pageButtonCancelProps,
  pageButtonDangerProps,
  pageButtonOutlinedDangerProps,
  filterHeaderActionSx,
} from "../../../lib/ui/pageButtons";
import { formatDateTime } from "../../../lib/datetime/displayDateTime";
import { DURATION_TYPE_OPTIONS } from "../../../lib/ui/iconSelectOptions";
import { DRAWER_FORM_GRID_SPACING } from "../../../lib/ui/dialogFormLayout";

const formatDateShort = (value?: string) => formatDateTime(value, "date") || "—";

const emptyAssignForm = (): AssignSubscriptionRequest => ({
  username: "",
  planId: "",
  durationType: "RECURRING",
  recurringMonths: 12,
  startDate: new Date().toISOString().slice(0, 10),
});

const canCancel = (status?: string) => status === "ACTIVE" || status === "PASSIVE";

const statusChipColor = (s?: string) => {
  if (s === "ACTIVE") return "success" as const;
  if (s === "PASSIVE") return "warning" as const;
  if (s === "CANCELLED") return "default" as const;
  return "default" as const;
};

const notify = (content: string, color: "success" | "error" | "warning" = "success") => {
  window.dispatchEvent(
    new CustomEvent("showGlobalNotification", {
      detail: {
        color,
        icon: color === "success" ? "check_circle" : color === "warning" ? "warning" : "error",
        title: i18n.t("routes.subscriptionAdmin"),
        reason: "API",
        content,
        info: "SUBSCRIPTION",
      },
    })
  );
};

const formatPeriod = (quota?: PeriodQuota) => {
  if (!quota) return "—";
  const remaining = quota.remaining ?? 0;
  const limit = quota.limit ?? 0;
  if (limit === -1 || remaining === -1) return "∞";
  return `${remaining}/${limit}`;
};

const quotaLine = (t: typeof i18n.t, daily?: PeriodQuota, weekly?: PeriodQuota, monthly?: PeriodQuota) =>
  t("subscriptionAdmin.quotaLine", {
    daily: `${t("subscription.daily")} ${formatPeriod(daily)}`,
    weekly: `${t("subscription.weekly")} ${formatPeriod(weekly)}`,
    monthly: `${t("subscription.monthly")} ${formatPeriod(monthly)}`,
  });

const getSubscriptionReadCols = (t: typeof i18n.t) => [
  {
    name: t("subscription.user"),
    selector: (row: UserSubscription) => row.username || "—",
    sortable: true,
  },
  {
    name: t("subscription.plan"),
    selector: (row: UserSubscription) => row.planSnapshot?.name || row.planId || "—",
    sortable: true,
  },
  {
    name: t("subscription.status"),
    selector: (row: UserSubscription) => row.status || "—",
    sortable: true,
    cell: (row: UserSubscription) => (
      <Chip
        size="small"
        label={
          row.status
            ? t(`options.subscriptionStatus.${row.status}`, { defaultValue: row.status })
            : "—"
        }
        color={statusChipColor(row.status)}
        variant="outlined"
        sx={{ fontWeight: 600 }}
      />
    ),
  },
  {
    name: t("subscription.start"),
    selector: (row: UserSubscription) => formatDateShort(row.startDate),
    sortable: true,
  },
  {
    name: t("subscription.end"),
    selector: (row: UserSubscription) => formatDateShort(row.endDate),
    sortable: true,
  },
];

function SubscriptionAdminPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const datatable = useRef<{ refresh?: () => void } | null>(null);
  const subscriptionReadCols = useMemo(() => getSubscriptionReadCols(t), [t]);

  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignForm, setAssignForm] = useState<AssignSubscriptionRequest>(emptyAssignForm);

  const [inspectUsername, setInspectUsername] = useState<string | null>(null);
  const [inspectSubs, setInspectSubs] = useState<UserSubscription[]>([]);
  const [inspectQuota, setInspectQuota] = useState<QuotaStatusResponse | null>(null);
  const [inspectLoading, setInspectLoading] = useState(false);
  const [quotaOpen, setQuotaOpen] = useState(false);

  const [cancelTarget, setCancelTarget] = useState<UserSubscription | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);

  const refreshDirectory = useCallback(() => {
    datatable.current?.refresh?.();
  }, []);

  const loadActivePlans = useCallback(async () => {
    const res = await subscriptionPlanService.findByStatus("ACTIVE");
    setPlans(Array.isArray(res) ? res : []);
  }, []);

  useEffect(() => {
    loadActivePlans().catch(() => setPlans([]));
  }, [loadActivePlans]);

  const loadInspect = useCallback(async (username: string) => {
    setInspectLoading(true);
    try {
      const [subs, quota] = await Promise.all([
        userSubscriptionService.byUsername(username),
        quotaService.quotaByUsername(username).catch(() => null),
      ]);
      setInspectSubs(Array.isArray(subs) ? subs : []);
      setInspectQuota(quota);
    } catch (e: any) {
      setInspectSubs([]);
      setInspectQuota(null);
      notify(e?.response?.data?.message || t("subscriptionAdmin.lookupFailed"), "error");
    } finally {
      setInspectLoading(false);
    }
  }, [t]);

  const openInspect = useCallback(
    (item: UserSubscription) => {
      const username = String(item.username || "").trim();
      if (!username) return;
      setQuotaOpen(false);
      setInspectUsername(username);
      void loadInspect(username);
    },
    [loadInspect]
  );

  const closeInspect = () => {
    setInspectUsername(null);
    setInspectSubs([]);
    setInspectQuota(null);
    setQuotaOpen(false);
  };

  const openAssign = (username = "") => {
    setAssignForm({
      ...emptyAssignForm(),
      username,
    });
    setAssignOpen(true);
  };

  const closeAssign = () => {
    if (assignLoading) return;
    setAssignOpen(false);
  };

  const assignPlan = async () => {
    const username = assignForm.username.trim();
    if (!username || !assignForm.planId || !assignForm.startDate) return;
    setAssignLoading(true);
    try {
      await userSubscriptionService.assign({
        ...assignForm,
        username,
        recurringMonths: assignForm.durationType === "RECURRING" ? assignForm.recurringMonths : undefined,
      });
      notify(t("subscriptionAdmin.assignSuccess"));
      setAssignOpen(false);
      refreshDirectory();
      setInspectUsername(username);
      setQuotaOpen(false);
      await loadInspect(username);
    } catch (e: any) {
      notify(e?.response?.data?.message || t("subscriptionAdmin.assignFailed"), "error");
    } finally {
      setAssignLoading(false);
    }
  };

  const openCancelDialog = (item: UserSubscription) => {
    setCancelTarget(item);
    setCancelReason("");
  };

  const closeCancelDialog = () => {
    if (cancelLoading) return;
    setCancelTarget(null);
    setCancelReason("");
  };

  const submitCancel = async () => {
    if (!cancelTarget?.id || !cancelReason.trim()) return;
    setCancelLoading(true);
    try {
      await userSubscriptionService.cancel(cancelTarget.id, { reason: cancelReason.trim() });
      notify(t("subscriptionAdmin.cancelSuccess"));
      const username = cancelTarget.username?.trim() || inspectUsername;
      closeCancelDialog();
      refreshDirectory();
      if (username) {
        await loadInspect(username);
      }
    } catch (e: any) {
      notify(e?.response?.data?.message || t("subscription.cancelFailed"), "error");
    } finally {
      setCancelLoading(false);
    }
  };

  const assignReady =
    Boolean(assignForm.username.trim()) && Boolean(assignForm.planId) && Boolean(assignForm.startDate);

  const inspectSubtitle = inspectQuota?.planName || t("subscriptionAdmin.inspectContent");

  return (
    <>
      <PageListContainer>
        <DataTableWithPagination
          ref={datatable}
          cols={subscriptionReadCols}
          fetchUrl="/user-subscription/read"
          searchKey="username"
          searchPlaceholder={t("subscription.filterUsername")}
          handleView={openInspect}
          handleSelect={openInspect}
          fetchReadRecordOnEdit={false}
          listFields={["id", "username", "planId", "planSnapshot", "status", "startDate", "endDate", "durationType", "recurringMonths"]}
          headerActions={
            <>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => navigate("/plans")}
                sx={filterHeaderActionSx}
              >
                {t("subscriptionAdmin.openPlans")}
              </Button>
              <PageCreateButton onClick={() => openAssign()}>{t("subscriptionAdmin.assign")}</PageCreateButton>
            </>
          }
        />
      </PageListContainer>

      <RightDrawer
        open={Boolean(inspectUsername)}
        onClose={closeInspect}
        width="sm"
        title={t("subscriptionAdmin.inspectTitle", { username: inspectUsername || "—" })}
        subtitle={inspectSubtitle}
        footer={
          <>
            <Button {...drawerButtonCancelProps} type="button" onClick={closeInspect}>
              {t("common.close")}
            </Button>
            <Button
              {...drawerButtonSaveProps}
              type="button"
              onClick={() => openAssign(inspectUsername || "")}
            >
              {t("subscriptionAdmin.assignToUser")}
            </Button>
          </>
        }
      >
        {inspectLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <DrawerForm>
            <DrawerFormSection title={t("subscriptionAdmin.subscriptions")}>
              {inspectSubs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t("subscriptionAdmin.noSubscriptions")}
                </Typography>
              ) : (
                <Stack divider={<Divider />}>
                  {inspectSubs.map((item) => {
                    const durationLabel = item.durationType
                      ? t(`options.durationType.${item.durationType}`, { defaultValue: item.durationType })
                      : "";
                    const cycleLabel =
                      item.durationType === "RECURRING" && item.recurringMonths
                        ? t("subscription.cycleMonths", { count: item.recurringMonths })
                        : "";
                    return (
                      <Box key={item.id || `${item.username}-${item.startDate}`} sx={{ py: 1.25 }}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
                              <Typography variant="body2" fontWeight={600} noWrap title={item.planSnapshot?.name}>
                                {item.planSnapshot?.name || item.planId || "—"}
                              </Typography>
                              <Chip
                                size="small"
                                label={
                                  item.status
                                    ? t(`options.subscriptionStatus.${item.status}`, { defaultValue: item.status })
                                    : "—"
                                }
                                color={statusChipColor(item.status)}
                                variant="outlined"
                                sx={{ height: 22, fontWeight: 600 }}
                              />
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                              {t("subscriptionAdmin.term", {
                                start: formatDateShort(item.startDate),
                                end: formatDateShort(item.endDate),
                              })}
                              {durationLabel ? ` · ${durationLabel}` : ""}
                              {cycleLabel ? ` · ${cycleLabel}` : ""}
                            </Typography>
                          </Box>
                          {canCancel(item.status) && (
                            <Button
                              {...pageButtonOutlinedDangerProps}
                              size="small"
                              onClick={() => openCancelDialog(item)}
                              sx={{ textTransform: "none", fontWeight: 600, flexShrink: 0 }}
                            >
                              {t("common.cancel")}
                            </Button>
                          )}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </DrawerFormSection>

            <Box>
              <Button
                type="button"
                variant="text"
                size="small"
                onClick={() => setQuotaOpen((open) => !open)}
                endIcon={quotaOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                sx={{ px: 0, textTransform: "none", fontWeight: 600 }}
              >
                {t("subscriptionAdmin.quota")}
              </Button>
              {quotaOpen ? (
                inspectQuota ? (
                  <Stack spacing={0.75} sx={{ mt: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                        {t("subscription.chat")}
                      </Typography>
                      <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {quotaLine(
                          t,
                          inspectQuota.chatMessageDaily,
                          inspectQuota.chatMessageWeekly,
                          inspectQuota.chatMessageMonthly
                        )}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                        {t("subscription.mailAnalysis")}
                      </Typography>
                      <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {quotaLine(
                          t,
                          inspectQuota.mailAnalysisDaily,
                          inspectQuota.mailAnalysisWeekly,
                          inspectQuota.mailAnalysisMonthly
                        )}
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t("subscriptionAdmin.noQuota")}
                  </Typography>
                )
              ) : null}
            </Box>
          </DrawerForm>
        )}
      </RightDrawer>

      <RightDrawer
        open={assignOpen}
        onClose={closeAssign}
        width="sm"
        title={t("subscriptionAdmin.assignTitle")}
        subtitle={t("subscriptionAdmin.assignContent")}
        component="form"
        componentProps={{
          onSubmit: (e: React.FormEvent) => {
            e.preventDefault();
            void assignPlan();
          },
        }}
        footer={
          <>
            <Button {...drawerButtonCancelProps} type="button" onClick={closeAssign} disabled={assignLoading}>
              {t("common.cancel")}
            </Button>
            <Button {...drawerButtonSaveProps} type="submit" disabled={assignLoading || !assignReady}>
              {assignLoading ? t("subscriptionAdmin.working") : t("subscriptionAdmin.assignAction")}
            </Button>
          </>
        }
      >
        <DrawerForm>
          <DrawerFormSection title={t("subscription.planAndTerm")}>
            <Grid container spacing={DRAWER_FORM_GRID_SPACING}>
              <Grid size={12}>
                <TextField
                  required
                  size="small"
                  fullWidth
                  autoComplete="off"
                  label={t("subscription.username")}
                  value={assignForm.username}
                  onChange={(e) => setAssignForm((prev) => ({ ...prev, username: e.target.value }))}
                />
              </Grid>
              <Grid size={12}>
                <Autocomplete
                  size="small"
                  options={plans}
                  getOptionLabel={(option) => option.name || option.id || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={plans.find((plan) => plan.id === assignForm.planId) || null}
                  onChange={(_, value) =>
                    setAssignForm((prev) => ({ ...prev, planId: value?.id ? String(value.id) : "" }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} required label={t("subscription.plan")} />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: assignForm.durationType === "RECURRING" ? 6 : 12 }}>
                <IconAutocomplete
                  options={DURATION_TYPE_OPTIONS}
                  value={assignForm.durationType}
                  onChange={(value) =>
                    setAssignForm((prev) => ({ ...prev, durationType: (value || "RECURRING") as DurationType }))
                  }
                  label={t("subscription.duration")}
                  disableClearable
                />
              </Grid>
              {assignForm.durationType === "RECURRING" && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    size="small"
                    fullWidth
                    required
                    label={t("subscription.months")}
                    type="number"
                    inputProps={{ min: 1, max: 120 }}
                    value={assignForm.recurringMonths ?? 12}
                    onChange={(e) =>
                      setAssignForm((prev) => ({ ...prev, recurringMonths: Number(e.target.value) }))
                    }
                  />
                </Grid>
              )}
              <Grid size={12}>
                <TextField
                  required
                  size="small"
                  fullWidth
                  label={t("subscription.start")}
                  type="date"
                  value={assignForm.startDate || ""}
                  onChange={(e) => setAssignForm((prev) => ({ ...prev, startDate: e.target.value }))}
                />
              </Grid>
            </Grid>
          </DrawerFormSection>
        </DrawerForm>
      </RightDrawer>

      <Dialog open={cancelTarget != null} onClose={closeCancelDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t("subscriptionAdmin.cancelTitle")}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {t("subscriptionAdmin.cancelHint", {
              username: cancelTarget?.username || "—",
              plan: cancelTarget?.planSnapshot?.name || cancelTarget?.planId || "—",
            })}
          </Alert>
          <TextField
            autoFocus
            fullWidth
            size="small"
            multiline
            minRows={2}
            required
            label={t("subscription.reasonRequired")}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button {...pageButtonCancelProps} onClick={closeCancelDialog} disabled={cancelLoading}>
            {t("common.cancel")}
          </Button>
          <Button
            {...pageButtonDangerProps}
            onClick={submitCancel}
            disabled={cancelLoading || !cancelReason.trim()}
          >
            {cancelLoading ? t("subscriptionAdmin.working") : t("subscriptionAdmin.cancelConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SubscriptionAdminPage;
